<template>
  <div class="m-table" ref="containerRef">
    <el-table
      ref="table"
      :data="tableData"
      v-loading="loading"
      element-loading-background="rgba(0, 0, 0, 0.5)"
      :span-method="spanMethod"
      :cell-class-name="cellClassName"
      :height="height"
      :gutter-width="gutterWidth"
      :max-height="maxHeight"
      :highlight-current-row="highlightCurrentRow"
      @selection-change="multiSelectionChange"
      @row-contextmenu="handleRowContextmenu"
      :border="border"
      :default-expand-all="defaultExpandAll"
      :show-header="showHeader"
    >
      <el-table-column
        v-if="indexConfig.enable"
        :label="indexConfig.label || '序号'"
        :width="indexConfig.width || 50"
        :align="indexConfig.align || 'center'"
        type="index"
      ></el-table-column>
      <el-table-column
        v-if="multiSelection"
        align="center"
        type="selection"
        width="55px"
        :selectable="selectable"
      ></el-table-column>

      <el-table-column v-if="hasExpand" type="expand">
        <template slot-scope="props">
          <slot name="expand" :props="props"></slot>
        </template>
      </el-table-column>

      <template v-for="col in tableColumn">
        <el-table-column
          v-if="col.slot"
          :key="col.prop"
          :prop="col.prop"
          :label="col.label | translateTo(i18nPath)"
          :width="col.width"
          :show-overflow-tooltip="col.overflowTooltip"
          align="center"
        >
          <template slot-scope="{ row }">
            <slot :name="col.slot" :value="row[col.prop]" :row="row" :config="col"></slot>
          </template>
        </el-table-column>
        <el-table-column
          v-else-if="col.temp"
          :key="col.prop"
          :prop="col.prop"
          :label="col.label | translateTo(i18nPath)"
          :width="col.width"
          :show-overflow-tooltip="col.overflowTooltip"
          align="center"
        >
          <template slot-scope="scope">
            <span v-if="typeof col.temp === 'function'" v-html="col.temp(scope)"></span>
            <template v-if="typeof col.temp === 'object'">
              <el-popover
                v-if="col.temp.type === 'image' && col.temp.data[scope.$index]"
                trigger="hover"
                placement="top"
              >
                <el-image
                  :src="col.temp.data[scope.$index]"
                  :preview-src-list="col.temp.data.filter(e => e)"
                  style="width: 100px; height: 100px"
                ></el-image>
                <el-button slot="reference">{{ '图片' | translateTo(i18nPath) }}</el-button>
              </el-popover>
              <router-link v-else-if="col.temp.type === 'route'" :to="col.temp.route(scope)">
                {{ scope.row[col.prop] }}
              </router-link>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          v-else
          :key="col.prop"
          :prop="col.prop"
          :label="col.label | translateTo(i18nPath)"
          :width="col.width"
          align="center"
          :show-overflow-tooltip="col.overflowTooltip"
        ></el-table-column>
      </template>
      <el-table-column
        v-if="oprateMenu"
        :label="oprateMenu.label | translateTo(i18nPath)"
        :width="getWidth(oprateMenu.width)"
        align="center"
      >
        <template slot-scope="scope">
          <div class="button-box">
            <template v-for="(m, i) in oprateMenu.list">
              <m-tooltip
                v-if="m.notShow ? !m.notShow(scope) : true"
                :row-data="scope.row"
                :content="m.tooltip | translateTo(i18nPath)"
                :access="m.access"
                :key="i"
              >
                <el-button
                  :loading="scope.row[`opRef-${scope.$index}-${i}`]"
                  :disabled="m.disabled && m.disabled(scope)"
                  :type="m.text ? 'primary' : 'text'"
                  size="mini"
                  @click="
                    m.event(scope.$index, scope.row, () => {
                      return {
                        open() {
                          $set(scope.row, `opRef-${scope.$index}-${i}`, true);
                        },
                        close() {
                          $set(scope.row, `opRef-${scope.$index}-${i}`, false);
                        }
                      };
                    })
                  "
                >
                  <span :class="m.icon">{{ getBtnText(m, scope) }}</span>
                </el-button>
              </m-tooltip>
            </template>
          </div>
        </template>
      </el-table-column>
      <div slot="empty">
        <slot name="empty">{{ translateTo('暂无数据') }}</slot>
      </div>
    </el-table>
    <el-row type="flex" justify="center" style="margin-top: 20px" v-if="hasPagination">
      <el-pagination
        background
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page.sync="pagiOption.offset"
        :page-sizes="pagiOption.pagiSize"
        :page-size.sync="pagiOption.limit"
        :layout="pagiOption.layout"
        :total="pagiOption.total"
      ></el-pagination>
    </el-row>
  </div>
</template>

<script>
import { isArray, isFunction } from '@/utils/is';
import MTooltip from './m-tooltip.vue';
import { translateTo } from '@/utils/locale';
import { debounce } from '@/utils/debounce_throttle';

export default {
  name: 'MTable',
  components: {
    MTooltip
  },
  props: {
    i18nPath: String,
    tableData: Array,
    tableColumn: Array,
    oprateMenu: Object,
    pagiOption: Object,
    loading: Boolean,
    spanMethod: Function,
    cellClassName: Function,
    selectable: Function,
    height: String,
    gutterWidth: Number,
    defaultExpandAll: {
      type: Boolean,
      default: false
    },
    hasExpand: {
      type: Boolean,
      default: false
    },
    highlightCurrentRow: {
      type: Boolean,
      default: false
    },
    indexConfig: {
      type: Object,
      default: () => ({ enable: false })
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    border: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      type: String,
      default: '100%'
    },
    hasPagination: {
      type: Boolean,
      default: true
    },
    multiSelection: {
      type: [Array, null],
      default: null
    }
  },
  filters: {
    translateTo(val, i18nPath) {
      if (i18nPath) {
        return translateTo(val);
      } else {
        return val;
      }
    }
  },
  data() {
    return {
      handleResize: debounce(function () {
        this.$refs.table?.doLayout();
      }, 200)
    };
  },
  methods: {
    clearSelection() {
      this.$refs.table.clearSelection();
    },
    toggleRowSelection(e, flag) {
      this.$refs.table.toggleRowSelection(e, flag);
    },
    handleRowContextmenu(...params) {
      this.$emit('row-contextmenu', ...params);
    },
    handleSizeChange(val) {
      this.pagiOption.limit = val;
      this.$emit('search');
    },
    handleCurrentChange(val) {
      this.pagiOption.offset = val;
      this.$emit('search');
    },
    initSelection(n) {
      if (!isArray(n)) return;
      n.forEach(e => {
        this.$refs.table.toggleRowSelection(e, true);
      });
    },
    multiSelectionChange(val) {
      this.multiSelection.splice(0, Infinity, ...val);
    },
    selectAll() {
      this.$refs.table.toggleAllSelection();
    },
    getWidth(width) {
      if (isFunction(width)) return width();

      return width;
    },
    getBtnText(e, scope) {
      if (isFunction(e.text)) return this.$t(e.text(scope.row));

      return this.$t(e.text);
    }
  },
  watch: {
    multiSelection: {
      handler: 'initSelection',
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.initSelection();

    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });

    this.resizeObserver.observe(this.$refs.containerRef);
  }
};
</script>

<style lang="scss">
.m-table {
  --border-color: var(--mtable-border-color);
  --bg-color: var(--mtable-bg-color);
  --color: var(--mtable-color);
  --thead-color: var(--mtable-thead-color);

  --cell-bg-color: var(--bg-color);

  --wrap-border: var(--mtable-wrap-border);

  --thead-fs: var(--mtable-thead-fs);
  --tbody-fs: var(--mtable-tbody-fs);

  .el-table {
    font-size: var(--thead-fs);

    color: var(--color);

    background-color: var(--bg-color);

    &__body-wrapper {
      font-size: var(--tbody-fs);
    }

    &.el-table--border,
    &.el-table--group {
      border: var(--wrap-border);
    }

    th,
    tr {
      background-color: var(--cell-bg-color);
    }

    .cell {
      line-height: 1.5;
    }

    thead {
      color: var(--thead-color);
    }

    .el-table__empty-text {
      color: var(--color);
    }
  }

  .button-box {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    // & > div {
    //   width: 100%;
    // }
    .el-button {
      margin: 0 5px 5px 0;
    }
  }
}
</style>
